import { logger } from '@/services/LoggingService';

export type StoreLogLevel = 'debug' | 'info' | 'warn' | 'error';

// Centralized event names to avoid string literals scattered across modules
export const EVENTS = {
  // Hydration / Initialization
  HYDRATION_START: 'wealthica2:hydration:start',
  HYDRATION_COMPLETE: 'wealthica2:hydration:complete',
  HYDRATION_AUTO_REFETCH: 'wealthica2:hydration:auto_refetch',

  // Connections
  CONNECTION_ADDED: 'wealthica2:connection:added',
  CONNECTION_REMOVED: 'wealthica2:connection:removed',
  CONNECTION_STATUS_UPDATED: 'wealthica2:connection:state:change',
  ACTIVE_CONNECTION_SET: 'wealthica2:connection:active:set',
  CONNECTION_ERROR: 'wealthica2:connection:error',
  CACHE_REKEY: 'wealthica2:cache:rekey',

  // Accounts
  ACCOUNTS_SET: 'wealthica2:accounts:set',
  ACCOUNTS_CACHED: 'wealthica2:accounts:cached',
  ACCOUNTS_LOADING_SET: 'wealthica2:accounts:loading',
  ACCOUNTS_ERROR_SET: 'wealthica2:accounts:error',

  // Positions
  POSITIONS_SET: 'wealthica2:positions:set',
  POSITIONS_CACHED: 'wealthica2:positions:cached',
  POSITIONS_LOADING_SET: 'wealthica2:positions:loading',
  POSITIONS_ERROR_SET: 'wealthica2:positions:error',

  // Batch Ops
  BATCH_FETCH_START: 'wealthica2:batch:fetch:start',
  BATCH_FETCH_END: 'wealthica2:batch:fetch:end',

  // Restoration Coordination
  RESTORATION: 'wealthica2:restoration'
} as const;

const levelOrder: Record<StoreLogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40
};

function getConfiguredLevel(): StoreLogLevel {
  // Prefer explicit env configuration, fall back to dev-friendly default
  const metaEnv = (import.meta as unknown as { env?: Record<string, unknown> })?.env ?? {};
  const envLevel = metaEnv['VITE_LOG_LEVEL'] as string | undefined;
  const devFlag = Boolean(metaEnv['DEV']);
  const localLevel = typeof window !== 'undefined' ? window.localStorage.getItem('LOG_LEVEL') || undefined : undefined;
  const value = (envLevel || localLevel || (devFlag ? 'info' : 'warn')).toLowerCase();
  if (value === 'debug' || value === 'info' || value === 'warn' || value === 'error') return value;
  return 'warn';
}

function shouldLog(level: StoreLogLevel): boolean {
  try {
    const configured = getConfiguredLevel();
    return levelOrder[level] >= levelOrder[configured];
  } catch {
    return true;
  }
}

// Thin wrapper around LoggingService with environment-aware gates
export const storeLogger = {
  debug(component: string, message: string, details?: Record<string, unknown>) {
    if (!shouldLog('debug')) return Promise.resolve();
    return logger.debug(component, message, details);
  },
  info(component: string, message: string, details?: Record<string, unknown>) {
    if (!shouldLog('info')) return Promise.resolve();
    return logger.info(component, message, details);
  },
  warn(component: string, message: string, details?: Record<string, unknown>) {
    if (!shouldLog('warn')) return Promise.resolve();
    return logger.warn(component, message, details);
  },
  error(component: string, message: string, details?: Record<string, unknown>) {
    if (!shouldLog('error')) return Promise.resolve();
    return logger.error(component, message, details);
  }
};
